import React, { useEffect, useState, useCallback } from "react";
import { useParams, Navigate } from "react-router-dom";
import axios from "axios";
import SeoHead from "../components/SeoHead";
import { toast } from "sonner";
import HrefLangTags from "../components/HrefLangTags";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PricingCtaModal from "../components/PricingCtaModal";
import WebDevPortfolioSection from "../components/sections/WebDevPortfolioSection";
import ServiceHero from "../components/sections/ServiceHero";
import ProblemList from "../components/sections/ProblemList";
import SolutionList from "../components/sections/SolutionList";
import ServiceProcessSteps from "../components/sections/ServiceProcessSteps";
import PricingTiers from "../components/sections/PricingTiers";
import CaseStudyCard from "../components/sections/CaseStudyCard";
import RelatedReviews from "../components/sections/RelatedReviews";
import FAQAccordion from "../components/sections/FAQAccordion";
import CTASection from "../components/sections/CTASection";
import PostunHazirCTA from "../components/sections/PostunHazirCTA";
import { useLanguage } from "../contexts/LanguageContext";
import { useCurrency } from "../contexts/CurrencyContext";
import { useUTM } from "../hooks/useUTM";
import { fireQuickPricingLead } from "../lib/pricingCta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const VALID_SLUGS = ["meta-ads", "google-ads", "seo", "creative", "social-media", "web-development"];

export default function ServiceLandingPage() {
  const { slug, lang: routeLang } = useParams();
  const { lang, t } = useLanguage();
  const { format: formatPrice } = useCurrency();
  const { utm, referrer } = useUTM();
  const [service, setService] = useState(null);
  const [error, setError] = useState(false);
  const [modalPayload, setModalPayload] = useState(null);

  const scrollToContact = useCallback(() => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // Pricing tier "Bu paketi seç" → opens mini modal (name/phone) → /api/leads + WhatsApp
  const handlePricingSelect = useCallback((tier) => {
    if (!service) return;
    const tierKey = { starter: "tier_starter_name", pro: "tier_pro_name", premium: "tier_premium_name" }[tier.id] || "";
    const tierLabel = (service.content && service.content[tierKey]) || tier.id;
    const titlePieces = [service.content?.hero_title_pre, service.content?.hero_title_accent, service.content?.hero_title_post]
      .filter(Boolean).join(" ");
    const serviceLabel = titlePieces || slug;
    setModalPayload({
      serviceLabel,
      serviceSlug: slug,
      tierLabel,
      formattedPrice: formatPrice(tier.price_try),
      priceTry: tier.price_try,
    });
  }, [service, slug, formatPrice]);

  useEffect(() => {
    if (!VALID_SLUGS.includes(slug)) return;
    let cancelled = false;
    setService(null);
    setError(false);
    axios
      .get(`${API}/services/${slug}`, { params: { lang } })
      .then((res) => { if (!cancelled) setService(res.data); })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("service fetch failed", err);
        if (!cancelled) setError(true);
      });
    return () => { cancelled = true; };
  }, [slug, lang]);

  if (!VALID_SLUGS.includes(slug)) {
    return <Navigate to={`/${routeLang || "tr"}/`} replace />;
  }

  const content = service?.content || {};
  const titlePieces = [content.hero_title_pre, content.hero_title_accent, content.hero_title_post]
    .filter(Boolean).join(" ");
  // Use specific service name as title, not the eyebrow abbreviation
  const serviceDisplayName = titlePieces || content.hero_eyebrow || slug;
  const pageTitle = `${serviceDisplayName} — Yastık Medya`;
  const pageDesc = content.hero_subtitle || "";

  // Schema.org Service
  const serviceLd = service ? {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": titlePieces,
    "name": serviceDisplayName,
    "url": `https://yastikmedya.com/${lang}/services/${slug}`,
    "provider": { "@type": "Organization", "@id": "https://yastikmedya.com/#organization", "name": "Yastık Medya" },
    "areaServed": ["TR", "EU", "Global"],
    "description": pageDesc,
    "offers": (service.pricing || []).map((p) => ({
      "@type": "Offer", "price": p.price_try, "priceCurrency": "TRY",
    })),
  } : null;

  return (
    <div data-testid={`service-page-${slug}`} className="min-h-screen bg-cream text-ink">
      <SeoHead title={pageTitle} description={pageDesc} path={`/${lang}/services/${slug}`} imageKey={slug} lang={lang}>
        {serviceLd ? (
          <script type="application/ld+json">{JSON.stringify(serviceLd)}</script>
        ) : null}
      </SeoHead>
      <HrefLangTags pathSuffix={`services/${slug}`} />
      <Header />
      <main>
        {service ? (
          <>
            <ServiceHero service={service} content={content} onCtaClick={scrollToContact} />
            <ProblemList content={content} />
            <SolutionList content={content} />
            <ServiceProcessSteps content={content} />
            <PricingTiers
              pricing={service.pricing || []}
              content={content}
              onSelect={handlePricingSelect}
            />
            <CaseStudyCard content={content} />
            {/* Postun Hazır blue cross-sell — only on creative & meta-ads */}
            {(slug === "creative" || slug === "meta-ads") && <PostunHazirCTA />}
            {/* Web dev portfolio gallery — only on web-development slug */}
            {slug === "web-development" && <WebDevPortfolioSection />}
            <RelatedReviews tag={(service.review_tags || [])[0]} />
            <FAQAccordion content={content} />
            <CTASection
              eyebrow={content.final_cta_eyebrow}
              title={content.final_cta_title_pre}
              titleAccent={content.final_cta_title_accent}
              subtitle={content.final_cta_subtitle}
              defaultServiceSlug={slug}
              defaultServiceLabel={t(`services_options.${slug.replace("-", "_")}`)}
            />
          </>
        ) : error ? (
          <div className="container-page py-section min-h-[60vh] flex items-center">
            <p className="text-body text-ink/60">{t("service.load_error")}</p>
          </div>
        ) : (
          <div className="container-page py-section min-h-[60vh] flex items-center">
            <p className="text-body text-ink/60">{t("service.loading")}</p>
          </div>
        )}
      </main>
      <Footer />
      <PricingCtaModal
        open={!!modalPayload}
        onClose={() => setModalPayload(null)}
        payload={modalPayload}
      />
    </div>
  );
}
